// Validação visual dos passos (blocks) do editor de Automações (Workflow.js):
// detecta campos obrigatórios não preenchidos para sinalizar o card com borda
// vermelha — mesmo padrão do ChatbotBuilder (ver Chatbots/flowNodeValidation.js).
//
// Especialmente útil após importar uma automação de outro cliente: os campos
// vinculados ao cliente de origem são limpos e passam a aparecer em vermelho.
//
// i18n: este módulo não é um componente/hook, então não pode chamar
// useTranslation() diretamente (violaria as Rules of Hooks). Em vez disso, os
// validadores recebem `t` por parâmetro — o chamador (Workflow.js) passa o `t`
// obtido via useTranslation('automation'). Sem `t`, cai no fallback PT abaixo.

const isBlank = (v) =>
    v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);

// Fallback usado quando nenhum `t` é passado: apenas retorna o texto original.
const defaultT = (key, fallback) => fallback;

// Cada validador recebe o `data` do passo e o `t` (i18next) e retorna a lista de
// campos faltando. Lista vazia = passo completo. Só listamos passos com campos
// obrigatórios (incluindo os que carregam IDs de cliente); os demais são sempre completos.
const VALIDATORS = {
    'to.condition': (d, t) => (isBlank(d.conditions) ? [t('stepValidation.fields.conditions', 'Condições')] : []),

    'to.ai.agent': (d, t) => (isBlank(d.agentId) ? [t('stepValidation.fields.agent', 'Agente')] : []),

    'to.chat.sendMessage': (d, t) => {
        const missing = [];
        const type = d.chatTypeMessage || 'text';
        if (type === 'text' && isBlank(d.chatTextMessage)) missing.push(t('stepValidation.fields.message', 'Mensagem'));
        if (['file', 'image', 'audio'].includes(type) && isBlank(d.chatFiles)) missing.push(t('stepValidation.fields.file', 'Arquivo'));
        if (d.useChannel === true && isBlank(d.chatChannel)) missing.push(t('stepValidation.fields.channel', 'Canal'));
        return missing;
    },

    'to.chat.addNote': (d, t) => (isBlank(d.chatNoteText) ? [t('stepValidation.fields.noteText', 'Texto da nota')] : []),

    'to.chat.addTag': (d, t) => (isBlank(d.chatTags) ? [t('stepValidation.fields.tags', 'Etiquetas')] : []),

    'to.chat.removeTag': (d, t) => (isBlank(d.chatTagsRemove) ? [t('stepValidation.fields.tags', 'Etiquetas')] : []),

    'to.chat.transfer': (d, t) =>
        d.chatTypeTransfer === 'user'
            ? isBlank(d.chatUsersAssign) ? [t('stepValidation.fields.user', 'Usuário')] : []
            : isBlank(d.chatDepartamentsAssign) ? [t('stepValidation.fields.sector', 'Setor')] : [],

    'to.chat.assign': (d, t) =>
        d.chatTypeTransfer === 'user'
            ? isBlank(d.chatUsersAssign) ? [t('stepValidation.fields.user', 'Usuário')] : []
            : isBlank(d.chatDepartamentsAssign) ? [t('stepValidation.fields.sector', 'Setor')] : [],

    'to.moveCardStage': (d, t) => {
        const missing = [];
        if (isBlank(d.pipelineId)) missing.push(t('stepValidation.fields.funnel', 'Funil'));
        if (isBlank(d.stageId)) missing.push(t('stepValidation.fields.stage', 'Estágio'));
        return missing;
    },

    'to.funnel.action': (d, t) => (isBlank(d.funnelAction) ? [t('stepValidation.fields.leadAction', 'Ação no lead')] : []),

    'to.funnel.cardCreate': (d, t) => {
        const missing = [];
        if (isBlank(d.pipelineId)) missing.push(t('stepValidation.fields.funnel', 'Funil'));
        if (isBlank(d.stageId)) missing.push(t('stepValidation.fields.stage', 'Estágio'));
        return missing;
    },

    'to.funnel.cardTransferUser': (d, t) =>
        d.linkToChatUser ? [] : isBlank(d.cardTransferUsers) ? [t('stepValidation.fields.user', 'Usuário')] : [],

    'to.httpRequest': (d, t) => (isBlank(d.httpUrl) ? [t('stepValidation.fields.url', 'URL')] : []),
};

/**
 * Retorna os rótulos dos campos obrigatórios não preenchidos de um passo.
 * @param {{ value?: string, data?: object }} block
 * @param {(key: string, fallback: string) => string} [t] - função de tradução (i18next), opcional
 * @returns {string[]}
 */
export function getStepMissingFields(block, t = defaultT) {
    if (!block || !block.value) return [];
    const validator = VALIDATORS[block.value];
    return validator ? validator(block.data || {}, t) : [];
}

/** O passo tem campos obrigatórios não preenchidos? */
export function isStepIncomplete(block, t = defaultT) {
    return getStepMissingFields(block, t).length > 0;
}
